import { memo, MouseEvent, useCallback, useState } from 'react';
import { Badge, Box, CircularProgress, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { Close, FileDownloadOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCentralizedExport } from 'contexts/CentralizedExportProvider';
import { BackgroundExportItem } from 'modules/export/storage/background-export-queue.storage';

const BackgroundExportIndicator = () => {
  const { t } = useTranslation('common');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { updateBackgroundQueue, pollingData: queue } = useCentralizedExport();

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRemove = useCallback(
    (id: string) => {
      const updatedQueue = queue?.filter((item: BackgroundExportItem) => item._id !== id) || [];
      updateBackgroundQueue(updatedQueue);
    },
    [queue, updateBackgroundQueue],
  );

  const activeQueue = queue?.filter((item: BackgroundExportItem) => item.status !== 'DONE') || [];
  const pendingCount = activeQueue.filter((item: BackgroundExportItem) => item.status === 'PENDING').length;

  if (activeQueue.length === 0) {
    return null;
  }

  return (
    <Box className='relative flex items-center justify-center'>
      <IconButton onClick={handleClick} color='primary' className='relative flex items-center justify-center'>
        <Badge badgeContent={pendingCount} color='primary' className='absolute'>
          <FileDownloadOutlined />
        </Badge>
        {pendingCount > 0 ? <CircularProgress size={28} color='primary' className='absolute' /> : null}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: { minWidth: 300, maxWidth: 400, marginTop: 4 },
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant='h6'>
            {t('exportDialog.backgroundDownloads', { defaultValue: 'Descargas en segundo plano' })}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {t('exportDialog.backgroundDownloadsCount', {
              defaultValue: '{{count}} en cola',
              count: pendingCount,
            })}
          </Typography>
        </Box>
        {activeQueue.map((item: BackgroundExportItem) => (
          <MenuItem key={item._id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant='body2' noWrap>
                {item.name}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {item.status === 'PENDING' && t('exportDialog.status.pending', { defaultValue: 'Pendiente' })}
                {item.status === 'DONE' && t('exportDialog.status.done', { defaultValue: 'Completado' })}
                {item.status === 'ERROR' && t('exportDialog.status.error', { defaultValue: 'Error' })}
              </Typography>
            </Box>
            {item.status === 'PENDING' && <CircularProgress size={20} />}
            {item.status !== 'PENDING' && (
              <IconButton
                size='small'
                onClick={() => {
                  handleRemove(item._id);
                }}
              >
                <Close fontSize='small' />
              </IconButton>
            )}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default memo(BackgroundExportIndicator);
